/* ==========================================================================
   InputRxjs > Client > Prototype
   ========================================================================== */

/* Modules */
const { Store } = require('@bluekiri/alexandria/helpers/state');

const { fromEvent } = require('rxjs');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { initLoader, destroyLoader } = require('./loader');

const configuration = require('./config');

const isText = (input = HTMLInputElement) => input.type === 'text';
const isButton = (input = HTMLInputElement) => input.type === 'button';

/* Prototype */
const prototype = {
    /* Pairing label */
    setPairing() {
        if (this.hooks.label) {
            this.hooks.input.setAttribute('id', this.id);
            this.hooks.label.setAttribute('for', this.id);
        }

        return this;
    },
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            input: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => this.store.dispatch(actions.destroy()),
            disable: () => this.store.dispatch(actions.disable()),
            display: () => this.store.dispatch(actions.display()),
            enable: () => this.store.dispatch(actions.enable()),
            hide: () => this.store.dispatch(actions.hide()),
            resetStatus: () => this.store.dispatch(actions.resetStatus()),
            setUiState: data => this.store.dispatch(actions.setUiState(data)),
            setError: () => this.store.dispatch(actions.setError()),
            setValid: () => this.store.dispatch(actions.setValid()),
            toggleActive: () => this.store.dispatch(actions.toggleActive()),
            setValue: value => this.store.dispatch(actions.setValue(value)),
            changeValue: value => this.store.dispatch(actions.changeValue(value)),
            reset: () => this.store.dispatch(actions.reset())
        };

        return this;
    },
    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Loader */
                destroyLoader(this);

                /* Unsubscribe */
                this.unsubscribe();

                /* Remove from DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean instance */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),
            disable: this.store.subscribe((state, action) => {
                if (action.type !== types.DISABLE) return;

                this.element.classList.add(configuration().css.disabled);
                this.hooks.input.setAttribute('disabled', '');
            }),
            display: this.store.subscribe((state, action) => {
                if (action.type !== types.DISPLAY) return;

                this.element.classList.remove(configuration().css.hidden);
            }),
            enable: this.store.subscribe((state, action) => {
                if (action.type !== types.ENABLE) return;

                this.element.classList.remove(configuration().css.disabled);
                this.hooks.input.removeAttribute('disabled');
            }),
            hide: this.store.subscribe((state, action) => {
                if (action.type !== types.HIDE) return;

                this.element.classList.add(configuration().css.hidden);
            }),
            resetStatus: this.store.subscribe((state, action) => {
                if (action.type !== types.RESET_STATUS) return;

                this.element.classList.remove(configuration().css.valid);
                this.element.classList.remove(configuration().css.error);
            }),
            setError: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_ERROR) return;

                this.element.classList.remove(configuration().css.valid);
                this.element.classList.add(configuration().css.error);
            }),
            setValid: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_VALID) return;

                this.element.classList.remove(configuration().css.error);
                this.element.classList.add(configuration().css.valid);
            }),
            toggleActive: this.store.subscribe((state, action) => {
                if (action.type !== types.TOGGLE_ACTIVE) return;

                const { active } = this.store.select('input');

                if (active) {
                    this.element.classList.add(configuration().css.active);
                } else {
                    this.element.classList.remove(configuration().css.active);
                }
            }),
            setValue: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_VALUE) return;

                const { active } = state.input;

                if (!this.config.isAlternative && !active) this.actions.toggleActive();

                /* Set value to the input element */
                this.hooks.input.value = action.payload;
            }),
            changeValue: this.store.subscribe((state, action) => {
                if (action.type !== types.CHANGE_VALUE) return;

                const { active } = state.input;

                if (!this.config.isAlternative && !active) this.actions.toggleActive();
            }),
            reset: this.store.subscribe((state, action) => {
                if (action.type !== types.RESET) return;

                this.element.classList.remove(configuration().css.disabled);
                this.hooks.input.removeAttribute('disabled');

                this.element.classList.remove(configuration().css.active);

                /* Set value to the input element */
                this.hooks.input.value = null;
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {
            focusIn: fromEvent(this.element, 'focusin').subscribe(() => {
                const { active, value } = this.store.select('input');

                if (!active && !this.config.isAlternative) this.actions.toggleActive();

                if (!this.config.selectionRange) return;

                if (isText(this.hooks.input) && value) this.hooks.input.setSelectionRange(0, value.length);
            }),
            focusOut: fromEvent(this.element, 'focusout').subscribe(() => {
                const { active, value } = this.store.select('input');

                if (active && !value && !this.config.isAlternative) this.actions.toggleActive();
            })
        };

        /**
         * Add change subscription to inputs that are not buttons
         * so we can connect to it internal store so we can grab
         * the current value.
         * In case it is a button, we are going to modify its value
         * from outside
         */
        if (!isButton(this.hooks.input)) {
            this.subscriptions = {
                ...this.subscriptions,
                change: fromEvent(this.hooks.input, 'blur').subscribe(event => {
                    /* Prevent propagation */
                    event.stopPropagation();

                    /* Data */
                    const data = this.hooks.input.value;

                    /* Dispatch action */
                    this.actions.changeValue(data);
                })
            };
        }

        return this;
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        const { disabled, hidden, active } = this.config;
        const { value } = this.hooks.input;

        /* Sync store ui-state */
        this.actions.setUiState({ disabled, hidden, active });

        if (disabled) this.actions.disable();

        /* Sync value in case it has from first render */
        if (!value) return this;

        /* Dispatch action */
        this.actions.setValue(value);

        return this;
    },
    /* Init */
    init() {
        /* Set pairing */
        this.setPairing();

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* Loader */
        initLoader(this);

        /* Start */
        this.start();
    }
};

/* Exports */
module.exports = prototype;
